
import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { featuredDealService } from "@/services/api/cresivoxServer";
import { iFeaturedDealDTO, REDUCER_NAME } from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

export const createFeaturedDeal = createAsyncThunk<
  iGenericResponse<any | null> | null,
  FormData,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("createFeaturedDeal"),
  //@ts-ignore
  async (formData, { rejectWithValue }) => {
    try {
      const response = await featuredDealService.createFeaturedDeal(formData);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_CREATED) {
          return { httpStatusCode, data, message };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const updateFeaturedDeal = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { dealId: string; data: FormData },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("updateFeaturedDeal"),
   //@ts-ignore
  async ({ dealId, data }, { rejectWithValue }) => {
    try {
      const response = await featuredDealService.updateFeaturedDeal(dealId, data);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { httpStatusCode, data, message };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const getAllFeaturedDeals = createAsyncThunk<
  iGenericResponse<any[]>,
  void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getAllFeaturedDeals"),
  //@ts-ignore
  async (_, { rejectWithValue }) => {
    try {
      const response = await featuredDealService.getAllFeaturedDeals();
      if (!response) {
        return rejectWithValue({ message: "No response received" });
      }
      
      
      // Handle different response structures
      let dataArray = [];
      if (Array.isArray(response.data)) {
        dataArray = response.data;
      } else if (response.data?.responseBody?.data) {
        dataArray = response.data.responseBody.data;
      } else if (response.data?.data) {
        dataArray = response.data.data;
      }

      return {
        httpStatusCode: response.httpStatusCode,
        data: dataArray,
        message: response.message
      };
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const toggleFeaturedDealStatus = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { dealId: string; status: boolean },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("toggleFeaturedDealStatus"),
   //@ts-ignore
  async ({ dealId, status }, { rejectWithValue }) => {
    try {
      const response = await featuredDealService.toggleFeaturedDealStatus(dealId, status);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { httpStatusCode, data, message };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const deleteFeaturedDeal = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { dealId: string },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("deleteFeaturedDeal"),
   //@ts-ignore
  async ({ dealId }, { rejectWithValue }) => {
    try {
      const response = await featuredDealService.deleteFeaturedDeal(dealId);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { httpStatusCode, data, message };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);


// Add these to your ThunkActions file

export const addProductToDeal = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { dealId: string; productId: string },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("addProductToDeal"),
  //@ts-ignore
  async ({ dealId, productId }, { rejectWithValue }) => {
    try {
      const response = await featuredDealService.addProductToDeal(dealId, productId);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_CREATED) {
          return { httpStatusCode, data, message };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const removeProductFromDeal = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { dealId: string; productId: string },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("removeProductFromDeal"),
  //@ts-ignore
  async ({ dealId, productId }, { rejectWithValue }) => {
    try {
      const response = await featuredDealService.removeProductFromDeal(dealId, productId);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { httpStatusCode, data, message };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const getDealProducts = createAsyncThunk<
  iGenericResponse<any[]>,
  string,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getDealProducts"),
  //@ts-ignore
  async (dealId, { rejectWithValue }) => {
    try {
      const response = await featuredDealService.getDealProducts(dealId);
      if (!response) {
        return rejectWithValue({ message: "No response received" });
      }
      return {
        httpStatusCode: response.httpStatusCode,
        data: response.data,
        message: response.message
      };
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);